import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import {ProjectStatisticRepository} from "../../../../repositories/ProjectStatisticRepository";
import {Panel, PanelHeading} from "./Panel";
import {csvFileName, exportCsv, ExportButton} from "./ExportButton";
import {DateRangePickerMenu} from "./DateRangePickerMenu";
import {QUICK_RANGES} from "../../../../components/date-range/QuickRangeMenu";

const DATE_TIME = "YYYY-MM-DDTHH:mm:ss";
const MAX_TAGS = 8;

// Bars fade from the accent colour, most used first
const BAR_COLORS = ["#8491F7", "#9AA6F1", "#ACB6F3", "#C1CAF8", "#D7DDFB"];

const defaultRange = () => {
  const today = QUICK_RANGES.find(item => item.label === "Today");
  const [start, end] = today.calc();
  return {label: "Today", start, end};
};

// HOURLY buckets read well up to ~a day and a half; anything longer falls back to daily buckets
const periodFor = (start, end) => (moment(end).diff(moment(start), "hours", true) <= 36 ? "HOURLY" : "DAYS");

// How often each tag appeared in the project's conversations (project statistic, totals by tag uid).
// tagNames: {uid: name} from the flow settings; uids without a name (deleted tags) are left out.
export const TagUsagePanel = ({projectId, filters, tagNames, refreshInterval}) => {
  const [range, setRange] = useState(defaultRange);
  const [totals, setTotals] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!projectId || !range?.start || !range?.end) return;
    const requestId = ++requestRef.current;
    try {
      const response = await ProjectStatisticRepository.getTags({
        projectId,
        filters,
        period: periodFor(range.start, range.end),
        start: moment(range.start).format(DATE_TIME),
        end: moment(range.end).format(DATE_TIME),
      });
      if (requestId === requestRef.current) setTotals(response?.data?.totals || {});
    } catch (e) {
      console.error("Error fetching tag usage:", e);
      if (requestId === requestRef.current) setTotals(prev => prev || {});
    }
  }, [projectId, filters, range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshInterval) return;
    const intervalId = setInterval(load, refreshInterval);
    return () => clearInterval(intervalId);
  }, [load, refreshInterval]);

  // Every named tag with its count, most used first
  const allTags = useMemo(() => {
    const byName = {};
    Object.entries(totals || {}).forEach(([uid, count]) => {
      const name = tagNames[uid];
      if (name) byName[name] = (byName[name] || 0) + (Number(count) || 0);
    });
    return Object.entries(byName)
      .map(([name, count]) => ({name, count}))
      .sort((a, b) => b.count - a.count);
  }, [totals, tagNames]);
  const tags = useMemo(() => allTags.filter(tag => tag.count > 0).slice(0, MAX_TAGS), [allTags]);

  const max = tags[0]?.count || 1;

  return (
    <Panel id={"tags"} sx={{p: 2.5}}>
      <PanelHeading
        kicker={"TOP INTENTS"}
        title={"Tag usage"}
        description={"What callers are talking about"}
        actions={<>
          <ExportButton
            disabled={!allTags.length}
            onClick={() => exportCsv(
              [["Tag", "Occurrences"], ...allTags.map(tag => [tag.name, tag.count])],
              csvFileName(`tag-usage-${range.label.toLowerCase().replace(/\s+/g, "-")}`)
            )}
          />
          <DateRangePickerMenu
            value={range}
            onChange={(next) => setRange(next || defaultRange())}
            allowClear={false}
            ariaLabel={"Tag usage range"}
          />
        </>}
      />

      <Stack gap={1.75} sx={{mt: 2.5}}>
        {totals === null && Array.from({length: 5}).map((_, index) => <Skeleton key={index} height={30}/>)}
        {totals !== null && tags.length === 0 && (
          <Typography sx={{py: 4, textAlign: "center", fontSize: 13, color: "#8A8AA0"}}>
            No tags used in this period
          </Typography>
        )}
        {tags.map((tag, index) => (
          <Box key={tag.name}>
            <Stack direction={"row"} justifyContent={"space-between"} gap={1} sx={{mb: 0.75}}>
              <Typography noWrap sx={{fontSize: 13, color: "#4A4A63"}}>{tag.name}</Typography>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{tag.count.toLocaleString()}</Typography>
            </Stack>
            <Box sx={{height: 8, borderRadius: 4, bgcolor: "#F2F2F7", overflow: "hidden"}}>
              <Box sx={{
                height: "100%", borderRadius: 4, width: `${Math.max(2, (tag.count / max) * 100)}%`,
                bgcolor: BAR_COLORS[Math.min(index, BAR_COLORS.length - 1)], transition: "width 0.3s",
              }}/>
            </Box>
          </Box>
        ))}
      </Stack>
    </Panel>
  );
};
